import { contactError, contactNotes } from '../lib/bookingContact';
import BookingContactFields from './BookingContactFields';
import { serviceCatalog, serviceCategories, getSelectedService } from '../lib/catalog';
import React, { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, Clock, MapPin, Users, Instagram, Check, ChevronRight, ChevronLeft, Sparkles, Info, ShoppingBag, Plus, Minus, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import apiService from '../services/apiService';
import { toast } from '../hooks/use-toast';

const BookingWizard = ({ availableSlots, onBookingComplete }) => {
  const [step, setStep] = useState(1);
  const [selectedServices, setSelectedServices] = useState(() => { const selected = getSelectedService(); return selected ? [selected] : []; });
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingDetails, setBookingDetails] = useState({
    phone: '',
    instagram: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [contactValidation, setContactValidation] = useState('');
  const [categoryFilter, setCategoryFilter] = useState(() => getSelectedService()?.category || 'lashlift');

  // Services principaux (sans les options)
  const mainServices = serviceCatalog;

  // Options disponibles
  const optionsAvailable = [];

  // Filtres par catégorie de service
  const categoryFilters = serviceCategories.map(category => ({ ...category, icon: '✦' }));

  // Obtenir les services filtrés
  const getFilteredServices = () => {
    return mainServices.filter(s => s.category === categoryFilter);
  };

  // Calculer le prix total
  const calculateTotalPrice = () => {
    const servicesTotal = selectedServices.reduce((sum, service) => sum + service.price, 0);
    const optionsTotal = selectedOptions.reduce((sum, option) => sum + (option.price * option.quantity), 0);
    return servicesTotal + optionsTotal;
  };

  // Gestion de la sélection des services
  const toggleServiceSelection = (service) => {
    const isSelected = selectedServices.some(s => s.id === service.id);
    if (isSelected) {
      setSelectedServices(selectedServices.filter(s => s.id !== service.id));
    } else {
      setSelectedServices([...selectedServices, service]);
    }
  };

  // Gestion des options
  const updateOptionQuantity = (optionId, change) => {
    const existingOption = selectedOptions.find(o => o.id === optionId);
    
    if (existingOption) {
      const newQuantity = existingOption.quantity + change;
      if (newQuantity <= 0) {
        setSelectedOptions(selectedOptions.filter(o => o.id !== optionId));
      } else {
        setSelectedOptions(selectedOptions.map(o => 
          o.id === optionId ? { ...o, quantity: newQuantity } : o
        ));
      }
    } else if (change > 0) {
      const option = optionsAvailable.find(o => o.id === optionId);
      setSelectedOptions([...selectedOptions, { ...option, quantity: 1 }]);
    }
  };

  const getOptionQuantity = (optionId) => {
    const option = selectedOptions.find(o => o.id === optionId);
    return option ? option.quantity : 0;
  };

  // Navigation
  const handleNext = () => {
    if (step === 1 && selectedServices.length === 0) {
      toast({
        title: "Sélection requise",
        description: "Veuillez sélectionner au moins un service",
        variant: "destructive"
      });
      return;
    }
    if (step === 2 && !selectedDate) {
      toast({
        title: "Date requise",
        description: "Veuillez sélectionner une date",
        variant: "destructive"
      });
      return;
    }
    if (step === 3 && !selectedSlot) {
      toast({
        title: "Créneau requis",
        description: "Veuillez sélectionner un créneau horaire",
        variant: "destructive"
      });
      return;
    }
    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleServiceSelect = (service) => {
    toggleServiceSelection(service);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
  };

  // Obtenir les dates disponibles
  const getAvailableDates = () => {
    const dates = {};
    availableSlots.forEach(slot => {
      const dateStr = new Date(slot.date).toDateString();
      if (!dates[dateStr]) {
        dates[dateStr] = {
          date: new Date(slot.date),
          slots: []
        };
      }
      dates[dateStr].slots.push(slot);
    });
    return Object.values(dates).sort((a, b) => a.date - b.date);
  };

  // Obtenir les créneaux pour une date donnée
  const getSlotsForDate = (date) => {
    if (!date) return [];
    const dateStr = date.toDateString();
    return availableSlots.filter(slot => 
      new Date(slot.date).toDateString() === dateStr
    ).sort((a, b) => a.start_time.localeCompare(b.start_time));
  };

  // Soumettre la réservation
  const handleSubmit = async () => {
    if (!selectedSlot) return;

    const error = contactError(bookingDetails);
    setContactValidation(error);
    if (error || submitting) return;
    setSubmitting(true);

    try {
      // Créer la description des services
      const servicesDescription = selectedServices.map(s => s.name).join(', ');
      const optionsDescription = selectedOptions.map(o => `${o.name} x${o.quantity}`).join(', ');
      const fullDescription = optionsDescription 
        ? `${servicesDescription} + Options: ${optionsDescription}`
        : servicesDescription;

      const totalPrice = calculateTotalPrice();

      // Pour l'instant, on envoie le premier service sélectionné comme service principal
      const mainService = selectedServices[0];

      const appointmentData = {
        slot_id: selectedSlot.id,
        service_name: fullDescription,
        service_price: totalPrice,
        notes: `${contactNotes(bookingDetails)}\n\n🛍️ Détails:\nServices: ${servicesDescription}${optionsDescription ? `\nOptions: ${optionsDescription}` : ''}`
      };

      await apiService.createAppointment(appointmentData);

      toast({
        title: "Réservation confirmée !",
        description: "Votre rendez-vous a été enregistré avec succès"
      });

      try { localStorage.removeItem('selectedService'); } catch {}

      // Réinitialiser le formulaire
      setStep(1);
      setSelectedServices([]);
      setSelectedOptions([]);
      setSelectedDate(null);
      setSelectedSlot(null);
      setBookingDetails({
    phone: '',
        instagram: '',
        notes: ''
      });

      // Notifier le parent pour rafraîchir les données
      if (onBookingComplete) {
        onBookingComplete();
      }

    } catch (error) {
      console.error('Erreur lors de la réservation:', error);
      toast({
        title: "Erreur",
        description: error.message || "Une erreur est survenue lors de la réservation",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Rendu de l'indicateur de progression
  const ProgressIndicator = () => (
    <div className="mb-8">
      <div className="flex items-center justify-between max-w-2xl mx-auto">
        {[1, 2, 3, 4].map((s, idx) => (
          <React.Fragment key={s}>
            <div className="flex flex-col items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300 ${
                step >= s 
                  ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-lg' 
                  : 'bg-gray-200 text-gray-400'
              }`}>
                {step > s ? <Check className="w-5 h-5" /> : s}
              </div>
              <div className={`mt-2 text-xs font-medium ${step >= s ? 'text-amber-600' : 'text-gray-400'}`}>
                {s === 1 && 'Services'}
                {s === 2 && 'Date'}
                {s === 3 && 'Heure'}
                {s === 4 && 'Confirmer'}
              </div>
            </div>
            {idx < 3 && (
              <div className={`flex-1 h-1 mx-2 rounded transition-all duration-300 ${
                step > s ? 'bg-gradient-to-r from-amber-500 to-amber-600' : 'bg-gray-200'
              }`} />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  // Modal Options
  const OptionsModal = () => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <CardHeader className="bg-gradient-to-r from-amber-500 to-amber-600 text-white">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <ShoppingBag className="w-6 h-6" />
              Ajouter des options
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowOptionsModal(false)}
              className="text-white hover:bg-white/20"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
          <CardDescription className="text-amber-50">
            Sélectionnez les options que vous souhaitez ajouter à votre réservation
          </CardDescription>
        </CardHeader>
        <CardContent className="overflow-y-auto p-6">
          <div className="space-y-4">
            {optionsAvailable.map((option) => {
              const quantity = getOptionQuantity(option.id);
              return (
                <Card key={option.id} className="border-2 hover:border-amber-400 transition-all">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="text-4xl">{option.icon}</div>
                      <div className="flex-1">
                        <h4 className="font-bold text-lg text-gray-800">{option.name}</h4>
                        <p className="text-sm text-gray-600">{option.description}</p>
                        <div className="text-xl font-bold text-amber-600 mt-2">{option.priceDisplay}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateOptionQuantity(option.id, -1)}
                          disabled={quantity === 0}
                          className="w-8 h-8 p-0"
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                        <div className="w-12 text-center font-bold text-lg">{quantity}</div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateOptionQuantity(option.id, 1)}
                          className="w-8 h-8 p-0 border-amber-500 text-amber-600 hover:bg-amber-50"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
        <div className="p-6 border-t bg-gray-50">
          <Button
            onClick={() => setShowOptionsModal(false)}
            className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white"
          >
            Valider la sélection
          </Button>
        </div>
      </Card>
    </div>
  );

  // ÉTAPE 1 : Choix de la prestation
  const renderStep1 = () => {
    const filteredServices = getFilteredServices();
    const totalPrice = calculateTotalPrice();

    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-2">
            Choisissez vos prestations
          </h2>
          <p className="text-gray-600">Sélectionnez un ou plusieurs services</p>
        </div>

        {/* Prix total affiché en haut */}
        {(selectedServices.length > 0 || selectedOptions.length > 0) && (
          <Card className="bg-gradient-to-r from-amber-500 to-amber-600 text-white border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm opacity-90">Prix total</div>
                  <div className="text-3xl font-bold">{totalPrice}€</div>
                </div>
                <div className="text-right">
                  <div className="text-sm opacity-90">{selectedServices.length} service(s)</div>
                  {selectedOptions.length > 0 && (
                    <div className="text-sm opacity-90">{selectedOptions.reduce((sum, o) => sum + o.quantity, 0)} option(s)</div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filtres */}
        <div className="flex flex-wrap gap-2 justify-center mb-6">
          {categoryFilters.map((filter) => (
            <Button
              key={filter.value}
              onClick={() => setCategoryFilter(filter.value)}
              variant={categoryFilter === filter.value ? 'default' : 'outline'}
              className={`${
                categoryFilter === filter.value
                  ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white border-0'
                  : 'bg-white border-gray-300 text-gray-700 hover:border-amber-400'
              } px-4 py-2 rounded-full font-medium transition-all duration-300`}
            >
              <span className="mr-2">{filter.icon}</span>
              {filter.label}
            </Button>
          ))}

        </div>

        {/* Grille des services */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredServices.map((service) => {
            const isSelected = selectedServices.some(s => s.id === service.id);
            return (
              <Card
                key={service.id}
                className={`cursor-pointer hover:shadow-xl transition-all duration-300 border-2 ${
                  isSelected 
                    ? 'border-amber-500 bg-amber-50 shadow-lg' 
                    : 'hover:border-amber-400'
                } group overflow-hidden`}
                onClick={() => handleServiceSelect(service)}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
                    <span className="text-2xl">{service.icon}</span>
                  </div>
                  {isSelected && (
                    <div className="absolute top-3 left-3 bg-amber-500 text-white rounded-full p-2">
                      <Check className="w-5 h-5" />
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                    <Badge className="bg-amber-500 text-white border-0">
                      {service.categoryDisplay}
                    </Badge>
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-amber-600 transition-colors">
                    {service.name}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {service.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-2xl font-bold text-amber-600">
                        {service.priceDisplay}
                      </div>
                      <div className="text-xs text-gray-500 flex items-center mt-1">
                        <Clock className="w-3 h-3 mr-1" />
                        {service.duration}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      className={`${
                        isSelected
                          ? 'bg-amber-500 hover:bg-amber-600'
                          : 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700'
                      } text-white border-0`}
                    >
                      {isSelected ? 'Sélectionné' : 'Sélectionner'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {filteredServices.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">Aucun service disponible pour ce filtre</p>
          </div>
        )}

        {/* Bouton suivant */}
        <div className="flex justify-end mt-8">
          <Button
            onClick={handleNext}
            disabled={selectedServices.length === 0}
            className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white px-8"
          >
            Continuer
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  };

  // ÉTAPE 2 : Choix de la date
  const renderStep2 = () => {
    const availableDates = getAvailableDates();
    const totalPrice = calculateTotalPrice();

    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-2">
            Choisissez une date
          </h2>
          <p className="text-gray-600">Sélectionnez le jour de votre rendez-vous</p>
        </div>

        {/* Services sélectionnés */}
        <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200">
          <CardContent className="p-4">
            <div className="space-y-2">
              <div className="font-bold text-gray-800 mb-3">Vos prestations:</div>
              {selectedServices.map((service, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <span>{service.icon}</span>
                    <span>{service.name}</span>
                  </span>
                  <span className="font-bold text-amber-600">{service.price}€</span>
                </div>
              ))}
              {selectedOptions.map((option, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <span>{option.icon}</span>
                    <span>{option.name} x{option.quantity}</span>
                  </span>
                  <span className="font-bold text-amber-600">{option.price * option.quantity}€</span>
                </div>
              ))}
              <div className="border-t pt-2 mt-2">
                <div className="flex items-center justify-between font-bold text-lg">
                  <span>Total:</span>
                  <span className="text-amber-600">{totalPrice}€</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Calendrier des dates */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {availableDates.map((dateObj, idx) => {
            const isSelected = selectedDate && selectedDate.toDateString() === dateObj.date.toDateString();
            const isToday = dateObj.date.toDateString() === new Date().toDateString();
            const isTomorrow = dateObj.date.toDateString() === new Date(Date.now() + 86400000).toDateString();

            return (
              <Card
                key={idx}
                className={`cursor-pointer hover:shadow-lg transition-all duration-300 border-2 ${
                  isSelected ? 'border-amber-500 bg-amber-50' : 'hover:border-amber-400'
                }`}
                onClick={() => handleDateSelect(dateObj.date)}
                role="button"
                tabIndex={0}
                aria-label={`Choisir le ${dateObj.date.toLocaleDateString('fr-FR')}`}
                aria-pressed={Boolean(isSelected)}
                onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); handleDateSelect(dateObj.date); } }}
              >
                <CardContent className="p-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-gray-800 mb-1">
                      {dateObj.date.getDate()}
                    </div>
                    <div className="text-sm text-gray-600 mb-3">
                      {dateObj.date.toLocaleDateString('fr-FR', { weekday: 'long', month: 'long' })}
                    </div>
                    {isToday && (
                      <Badge className="bg-blue-500 text-white mb-2">Aujourd'hui</Badge>
                    )}
                    {isTomorrow && (
                      <Badge className="bg-green-500 text-white mb-2">Demain</Badge>
                    )}
                    <div className="text-xs text-gray-500">
                      {dateObj.slots.length} créneau(x) disponible(s)
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {availableDates.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">Aucune date disponible pour le moment</p>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-8">
          <Button
            onClick={handleBack}
            variant="outline"
            className="px-8"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
          <Button
            onClick={handleNext}
            disabled={!selectedDate}
            className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white px-8"
          >
            Continuer
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  };

  // ÉTAPE 3 : Choix du créneau
  const renderStep3 = () => {
    const slotsForDate = getSlotsForDate(selectedDate);
    const totalPrice = calculateTotalPrice();

    // Grouper les créneaux par moment de la journée
    const morningSlots = slotsForDate.filter(s => {
      const hour = parseInt(s.start_time.split(':')[0]);
      return hour < 12;
    });
    const afternoonSlots = slotsForDate.filter(s => {
      const hour = parseInt(s.start_time.split(':')[0]);
      return hour >= 12 && hour < 18;
    });
    const eveningSlots = slotsForDate.filter(s => {
      const hour = parseInt(s.start_time.split(':')[0]);
      return hour >= 18;
    });

    const renderSlotGroup = (title, icon, slots) => {
      if (slots.length === 0) return null;

      return (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-2xl">{icon}</span>
            <h3 className="text-lg font-bold text-gray-800">{title}</h3>
            <span className="text-sm text-gray-500">({slots.length} créneaux)</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {slots.map((slot) => {
              const isSelected = selectedSlot && selectedSlot.id === slot.id;
              return (
                <Card
                  key={slot.id}
                  className={`cursor-pointer hover:shadow-lg transition-all duration-300 border-2 ${
                    isSelected ? 'border-amber-500 bg-amber-50' : 'hover:border-amber-400'
                  }`}
                  onClick={() => handleSlotSelect(slot)}
                  role="button"
                  tabIndex={0}
                  aria-label={`Choisir le créneau de ${slot.start_time}`}
                  aria-pressed={Boolean(isSelected)}
                  onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); handleSlotSelect(slot); } }}
                >
                  <CardContent className="p-4 text-center">
                    <div className="flex items-center justify-center gap-1 text-lg font-bold text-gray-800">
                      <Clock className="w-4 h-4" />
                      {slot.start_time}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {slot.end_time}
                    </div>
                    {isSelected && (
                      <Check className="w-5 h-5 text-amber-600 mx-auto mt-2" />
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      );
    };

    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-2">
            Choisissez un créneau
          </h2>
          <p className="text-gray-600">
            {selectedDate?.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
        </div>

        {/* Services et prix */}
        <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-gray-600">Total de votre réservation</div>
                <div className="text-2xl font-bold text-amber-600">{totalPrice}€</div>
              </div>
              <div className="text-right text-sm text-gray-600">
                <div>{selectedServices.length} service(s)</div>
                {selectedOptions.length > 0 && (
                  <div>{selectedOptions.reduce((sum, o) => sum + o.quantity, 0)} option(s)</div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Créneaux groupés */}
        {renderSlotGroup('Matin', '🌅', morningSlots)}
        {renderSlotGroup('Après-midi', '☀️', afternoonSlots)}
        {renderSlotGroup('Soir', '🌙', eveningSlots)}

        {slotsForDate.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">Aucun créneau disponible pour cette date</p>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-8">
          <Button
            onClick={handleBack}
            variant="outline"
            className="px-8"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
          <Button
            onClick={handleNext}
            disabled={!selectedSlot}
            className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white px-8"
          >
            Continuer
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  };

  // ÉTAPE 4 : Confirmation
  const renderStep4 = () => {
    const totalPrice = calculateTotalPrice();

    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-2">
            Confirmation de votre réservation
          </h2>
          <p className="text-gray-600">Vérifiez vos informations avant de confirmer</p>
        </div>

        {/* Récapitulatif */}
        <Card className="border-2 border-amber-200">
          <CardHeader className="bg-gradient-to-r from-amber-500 to-amber-600 text-white">
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              Récapitulatif
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            {/* Services */}
            <div>
              <div className="font-bold text-gray-800 mb-2">Services sélectionnés:</div>
              {selectedServices.map((service, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b">
                  <span className="flex items-center gap-2">
                    <span className="text-xl">{service.icon}</span>
                    <div>
                      <div className="font-medium">{service.name}</div>
                      <div className="text-xs text-gray-500">{service.duration}</div>
                    </div>
                  </span>
                  <span className="font-bold text-amber-600">{service.price}€</span>
                </div>
              ))}
            </div>

            {/* Options */}
            {selectedOptions.length > 0 && (
              <div>
                <div className="font-bold text-gray-800 mb-2">Options:</div>
                {selectedOptions.map((option, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 border-b">
                    <span className="flex items-center gap-2">
                      <span className="text-xl">{option.icon}</span>
                      <div>
                        <div className="font-medium">{option.name}</div>
                        <div className="text-xs text-gray-500">Quantité: {option.quantity}</div>
                      </div>
                    </span>
                    <span className="font-bold text-amber-600">{option.price * option.quantity}€</span>
                  </div>
                ))}
              </div>
            )}

            {/* Date et heure */}
            <div className="pt-4 border-t">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-600 mb-1">Date</div>
                  <div className="font-bold text-gray-800 flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4" />
                    {selectedDate?.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600 mb-1">Heure</div>
                  <div className="font-bold text-gray-800 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    {selectedSlot?.start_time} - {selectedSlot?.end_time}
                  </div>
                </div>
              </div>
            </div>

            {/* Prix total */}
            <div className="bg-amber-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-lg font-bold text-gray-800">Prix total:</span>
                <span className="text-3xl font-bold text-amber-600">{totalPrice}€</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Formulaire de détails */}
        <Card>
          <CardHeader>
            <CardTitle>Informations complémentaires</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {contactValidation && <p role="alert" className="text-red-700 text-sm">{contactValidation}</p>}
            <BookingContactFields value={bookingDetails} onChange={setBookingDetails} />

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium mb-2">Notes ou demandes spéciales (optionnel)</label>
              <Textarea
                placeholder="Ajoutez des informations supplémentaires..."
                value={bookingDetails.notes}
                onChange={(e) => setBookingDetails({...bookingDetails, notes: e.target.value})}
                rows={4}
              />
            </div>
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between mt-8">
          <Button
            onClick={handleBack}
            variant="outline"
            className="px-8"
            disabled={submitting}
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white px-8"
          >
            {submitting ? 'Confirmation en cours...' : 'Confirmer la réservation'}
            {!submitting && <Check className="w-4 h-4 ml-2" />}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full">
      <ProgressIndicator />
      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
      {step === 4 && renderStep4()}
      {showOptionsModal && <OptionsModal />}
    </div>
  );
};

export default BookingWizard;
